import { ref, computed, onMounted, onUnmounted } from 'vue';

export function useDeviceDetection() {
  const screenWidth = ref(window.innerWidth);
  const screenHeight = ref(window.innerHeight);

  // Update screen dimensions on resize
  const updateDimensions = () => {
    screenWidth.value = window.innerWidth;
    screenHeight.value = window.innerHeight;
  };

  // Basic device detection
  const isMobileUserAgent = computed(() => {
    return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });

  const isTouchDevice = computed(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  });

  // Screen size breakpoints
  const isMobileWidth = computed(() => screenWidth.value <= 768);
  const isTabletWidth = computed(() => screenWidth.value > 768 && screenWidth.value <= 1024);
  const isDesktopWidth = computed(() => screenWidth.value > 1024);

  // Device type detection
  const isMobile = computed(() => {
    return isMobileUserAgent.value || (isMobileWidth.value && isTouchDevice.value);
  });

  const isTablet = computed(() => {
    return isTabletWidth.value && isTouchDevice.value;
  });

  const isDesktop = computed(() => {
    return isDesktopWidth.value || (!isMobile.value && !isTablet.value);
  });

  // Orientation detection
  const isLandscape = computed(() => screenWidth.value > screenHeight.value);
  const isPortrait = computed(() => screenHeight.value >= screenWidth.value);

  // Specific device checks
  const isSmallMobile = computed(() => screenWidth.value <= 375);
  const isLargeMobile = computed(() => screenWidth.value >= 414 && screenWidth.value <= 768);

  // Combined context detection
  const deviceContext = computed(() => {
    if (isMobile.value) {
      if (isSmallMobile.value) return 'small-mobile';
      if (isLargeMobile.value) return 'large-mobile';
      return 'mobile';
    }
    if (isTablet.value) return 'tablet';
    return 'desktop';
  });

  // Widget positioning context
  const shouldUseMobileWidget = computed(() => {
    return isMobile.value || (isTablet.value && isPortrait.value);
  });

  const shouldUseDesktopWidget = computed(() => {
    return isDesktop.value || (isTablet.value && isLandscape.value);
  });

  // Chat popup sizing
  const getChatPopupFeatures = computed(() => {
    if (isMobile.value) {
      return 'width=100%,height=100%,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no,fullscreen=yes';
    }
    return 'width=450,height=650,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no';
  });

  // Lifecycle
  onMounted(() => {
    window.addEventListener('resize', updateDimensions, { passive: true });
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateDimensions);
  });

  return {
    // Reactive values
    screenWidth,
    screenHeight,
    
    // Device type
    isMobile,
    isTablet,
    isDesktop,
    
    // Screen sizes
    isMobileWidth,
    isTabletWidth,
    isDesktopWidth,
    isSmallMobile,
    isLargeMobile,
    
    // Orientation
    isLandscape,
    isPortrait,
    
    // Context
    deviceContext,
    isTouchDevice,
    isMobileUserAgent,
    
    // Widget selection
    shouldUseMobileWidget,
    shouldUseDesktopWidget,
    
    // Utilities
    getChatPopupFeatures
  };
} 